import { styled } from '@linaria/react';
import type { ReactNode } from 'react';
import { Switch } from '../../../../../ui-library/switch/switch';
import { SaveStatus } from '../../shared/settings/save-status';
import { SettingRow } from '../../shared/settings/setting-row';
import { SettingsCard } from '../../shared/settings/settings-card';
import { FloraSettingsSection } from '../agent-settings.type';
import { useFloraToolSettings } from '../use-flora-tool-settings';
import { SettingsSectionShell } from './settings-section-shell';

interface SettingsMemorySectionProps {
  /** The learned-preferences and memories lists, rendered below the chat-history setting. */
  children: ReactNode;
}

/**
 * Renders the Memory settings section: the "Reference chat history" switch
 * (when the organization offers it, autosaved; an error instead if the saved
 * settings didn't load), then what Flora has learned and remembered about the user.
 *
 * @param {SettingsMemorySectionProps} props - The lists to show under the setting
 * @returns {JSX.Element} The Memory section
 */
export function SettingsMemorySection({ children }: SettingsMemorySectionProps) {
  const { capabilities, draft, error, saveState, setReferenceChatHistory } = useFloraToolSettings();
  const isChatHistoryAvailable = Boolean(capabilities?.reference_chat_history_available);
  const showChatHistory = isChatHistoryAvailable && Boolean(draft);

  return (
    <SettingsSectionShell
      section={FloraSettingsSection.Memory}
      subtitle="What Flora remembers about you and how it uses your past chats."
      headerRight={<SaveStatus state={saveState} />}
    >
      {showChatHistory && draft && (
        <SettingsCard aria-label="Chat history">
          <SettingRow
            labelId="settings-reference-chat-history"
            label="Reference chat history"
            description="Let Flora learn from your chats to personalize answers."
            descriptionId="settings-reference-chat-history-description"
            control={
              <Switch
                on={draft.referenceChatHistory}
                onToggle={() => setReferenceChatHistory(!draft.referenceChatHistory)}
                size="lg"
                ariaLabel="Reference chat history"
                ariaDescribedBy="settings-reference-chat-history-description"
              />
            }
          />
        </SettingsCard>
      )}
      {/* Only where the setting applies, or where we can't tell because the catalog failed. */}
      {error && (isChatHistoryAvailable || !capabilities) && (
        <SettingsCard role="alert">
          <ErrorText>Couldn't load your chat history setting. Reload the page to try again.</ErrorText>
        </SettingsCard>
      )}
      <Lists>{children}</Lists>
    </SettingsSectionShell>
  );
}

const ErrorText = styled.p`
  margin: 0;
  padding: 16px 24px;
  font-size: var(--font-size-small);
  color: var(--ink-base);
`;

const Lists = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

export type { SettingsMemorySectionProps };
